import { AddBox } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    getSupplierActivationStatus,
    shouldShowEnableProductOrderingButton,
} from 'entities/CatalogItem';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { propertyActions } from 'entities/Property/model/slices/PropertySlice';
import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import {
    VendorActivationStatus,
    getPropertyVendorsMap,
} from 'entities/Vendors';
import {
    getRouteAddVendors,
    getRouteVendorsSupplierPage,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { ColoredLinkBox } from 'shared/ui/ColoredLinkBox';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';
import { SupplierRejectedTooltip } from 'shared/ui/Tooltip/ui/SupplierRejectedTooltip';
import { SupplierVerifyingTooltip } from 'shared/ui/Tooltip/ui/SupplierVerifyingTooltip';

interface SuppliersListProps {
    proposals: Proposal[];
    showSku?: boolean;
    bordered?: boolean;
}

export const SuppliersList = ({
    proposals,
    showSku = false,
    bordered = false,
}: SuppliersListProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const vendorsMap = useSelector(getPropertyVendorsMap);

    // NOTE: Renovations are not currently supported. When renovations feature
    // is added, uncomment this line and update the logic below to handle
    // renovationsAccountStatus checks.
    // const renovationEnabled = useFeatureFlag('renovation_enabled') || false;

    const handleEnableProductOrdering = useCallback(
        (proposal: Proposal) => {
            const vendor = vendorsMap?.[proposal.seller.uid];
            if (selectedPropertyId && vendor?.id) {
                // Vendor in list but not verified - navigate to supplier page and open enable product ordering modal
                navigate(
                    getRouteVendorsSupplierPage(vendor.id, selectedPropertyId),
                    { state: { openEnableProductOrdering: true } },
                );
            } else if (!vendor) {
                // Vendor not in list - navigate to Add Vendor page and open add-vendor modal for this supplier
                if (selectedPropertyId) {
                    dispatch(
                        propertyActions.setPropertyForVendor(
                            selectedPropertyId,
                        ),
                    );
                    navigate(getRouteAddVendors(selectedPropertyId), {
                        state: { openVendorByUid: proposal.seller.uid },
                    });
                }
            }
        },
        [dispatch, vendorsMap, navigate, selectedPropertyId],
    );

    return (
        <>
            {proposals?.map((proposal) => {
                const activationStatus = getSupplierActivationStatus(
                    proposal,
                    vendorsMap,
                );

                const showEnableButton = shouldShowEnableProductOrderingButton(
                    proposal,
                    vendorsMap,
                );

                return (
                    <Box
                        key={proposal.uid}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: showSku
                                ? 'space-between'
                                : 'flex-start',
                            gap: '8px',
                            padding: showSku ? '16px 0' : '0',
                            borderBottom: bordered
                                ? '1px solid #E9E8E8'
                                : undefined,
                            '&:hover': {
                                '& .enable-product-ordering-button': {
                                    opacity: showEnableButton ? 1 : 0,
                                },
                            },
                        }}
                        data-testclass={'supplier'}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    marginY: showSku ? '0' : '10px',
                                    color: showSku
                                        ? 'rgba(0, 0, 0, 0.5)'
                                        : 'rgba(0, 0, 0, 0.85)',
                                    fontFamily: showSku
                                        ? 'Open Sans'
                                        : undefined,
                                    fontSize: showSku ? '16px' : '14px',
                                    fontWeight: 400,
                                    lineHeight: showSku ? undefined : '20px',
                                }}
                                data-testclass={'name'}
                            >
                                {proposal.seller.name}
                            </Typography>

                            {activationStatus ===
                                VendorActivationStatus.VERIFIED && (
                                <Box sx={{ marginLeft: '10px' }}>
                                    <ColoredLinkBox
                                        data-testclass={
                                            'product-ordering-enabled'
                                        }
                                        color="#DBEFE1"
                                        tooltipText={t(
                                            'Product ordering enabled',
                                        )}
                                    />
                                </Box>
                            )}

                            {activationStatus ===
                                VendorActivationStatus.VERIFYING && (
                                <Box sx={{ marginLeft: '10px' }}>
                                    <SupplierVerifyingTooltip />
                                </Box>
                            )}

                            {activationStatus ===
                                VendorActivationStatus.INVALID && (
                                <Box sx={{ marginLeft: '10px' }}>
                                    <SupplierRejectedTooltip />
                                </Box>
                            )}

                            {showEnableButton && (
                                <IconButton
                                    className="enable-product-ordering-button"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEnableProductOrdering(proposal);
                                    }}
                                    disableRipple
                                    sx={{
                                        height: '40px',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        padding: '4px 0 0 0',
                                        marginLeft: '7px',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    data-testid="btn-enable-product-ordering"
                                >
                                    <DynamicTooltip
                                        content={
                                            <Typography typography="openSans.caption">
                                                {t('Enable product ordering')}
                                            </Typography>
                                        }
                                    >
                                        <AddBox
                                            sx={{
                                                fontSize: '26px',
                                                color: '#3F6EB7',
                                            }}
                                        />
                                    </DynamicTooltip>
                                </IconButton>
                            )}
                        </Box>
                        {showSku && (
                            <Typography
                                maxWidth="340px"
                                textAlign="right"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.85)',
                                    fontFamily: 'Open Sans',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    letterSpacing: '0.15px',
                                }}
                                data-testclass="sku"
                            >
                                {proposal.externalInvId || ''}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </>
    );
};
